import { offlineDb, type OfflineDb, type QueuedRegistration } from '@/lib/offline/offlineDb'
import type { ContactInput, RegistrationAnswerInput } from './registrationService'

export type NewRegistration = Readonly<{
  clientRegistrationId: string
  userId: string
  sessionId: string
  eventId: string
  ticketed?: boolean
  boxOffice?: { clientCheckInId: string; savedOffline: boolean } | undefined
  formId: string
  contact: ContactInput
  answers: RegistrationAnswerInput[]
  capturedAt: string
}>

export type OutboxCounts = Readonly<{ waiting: number; needsAttention: number }>

/** The device-side queue of registrations; every write is durable before it returns. */
export interface Outbox {
  add(registration: NewRegistration): Promise<void>
  counts(userId: string): Promise<OutboxCounts>
  /** Records ready to send now, oldest capture first. */
  due(userId: string, now: Date): Promise<QueuedRegistration[]>
  needingAttention(userId: string): Promise<QueuedRegistration[]>
  forEvent(userId: string, eventId: string): Promise<QueuedRegistration[]>
  /** Every registration this person still has on the device, at any event, newest first. */
  forCollector(userId: string): Promise<QueuedRegistration[]>
  markSyncing(id: string, now: Date): Promise<void>
  /** The server accepted it: the server now holds it, so the device copy goes. */
  markSynced(id: string): Promise<void>
  markRetry(id: string, error: string, nextAttemptAt: Date): Promise<void>
  markNeedsAttention(id: string, error: string): Promise<void>
  /** Send again: a person has looked at it, or the cause has changed. */
  retry(id: string): Promise<void>
  /** Only on a person's explicit decision. */
  discard(id: string): Promise<void>
  /**
   * The person who registered them removes a registration before it was sent.
   * False while it is being sent, when removing the copy here would not stop
   * the server receiving it.
   */
  withdraw(id: string): Promise<boolean>
  /** A sync interrupted by a closed tab or crash left records marked in flight. */
  recoverInterrupted(userId: string): Promise<number>
}

export function createOutbox(db: OfflineDb = offlineDb): Outbox {
  const table = db.registrations
  return {
    async add(registration) {
      await table.add({ ...registration, status: 'pending', attempts: 0, lastAttemptAt: null, nextAttemptAt: null, lastError: null })
    },
    async counts(userId) {
      const [pending, syncing, needsAttention] = await Promise.all([
        table.where('[userId+status]').equals([userId, 'pending']).count(),
        table.where('[userId+status]').equals([userId, 'syncing']).count(),
        table.where('[userId+status]').equals([userId, 'error']).count(),
      ])
      return { waiting: pending + syncing, needsAttention }
    },
    async due(userId, now) {
      const pending = await table.where('[userId+status]').equals([userId, 'pending']).toArray()
      return pending
        .filter((record) => !record.nextAttemptAt || record.nextAttemptAt <= now.toISOString())
        .sort((left, right) => left.capturedAt.localeCompare(right.capturedAt))
    },
    async needingAttention(userId) {
      const records = await table.where('[userId+status]').equals([userId, 'error']).toArray()
      return records.sort((left, right) => left.capturedAt.localeCompare(right.capturedAt))
    },
    async forEvent(userId, eventId) {
      const records = await table.where('[userId+eventId]').equals([userId, eventId]).toArray()
      return records.sort((left, right) => right.capturedAt.localeCompare(left.capturedAt))
    },
    async forCollector(userId) {
      const groups = await Promise.all((['pending', 'syncing', 'error'] as const).map((status) => table.where('[userId+status]').equals([userId, status]).toArray()))
      return groups.flat().sort((first, second) => second.capturedAt.localeCompare(first.capturedAt))
    },
    async markSyncing(id, now) {
      await table.where('clientRegistrationId').equals(id).modify((record) => {
        record.status = 'syncing'
        record.attempts += 1
        record.lastAttemptAt = now.toISOString()
      })
    },
    async markSynced(id) {
      await table.delete(id)
    },
    async markRetry(id, error, nextAttemptAt) {
      await table.update(id, { status: 'pending', lastError: error, nextAttemptAt: nextAttemptAt.toISOString() })
    },
    async markNeedsAttention(id, error) {
      await table.update(id, { status: 'error', lastError: error, nextAttemptAt: null })
    },
    async retry(id) {
      await table.update(id, { status: 'pending', nextAttemptAt: null })
    },
    async discard(id) {
      await table.delete(id)
    },
    async withdraw(id) {
      return db.transaction('rw', table, async () => {
        const record = await table.get(id)
        if (!record) return true
        if (record.status === 'syncing') return false
        await table.delete(id)
        return true
      })
    },
    async recoverInterrupted(userId) {
      return table.where('[userId+status]').equals([userId, 'syncing']).modify({ status: 'pending', nextAttemptAt: null })
    },
  }
}

export const outbox = createOutbox()
